import React from 'react';

// Линейные SVG-иконки нижней навигации (stroke = currentColor — красятся
// цветом текста, у активного пункта — фирменным цветом бренда).
// Используются брендами с pack.navIconStyle === 'line'; дефолт — эмодзи.
const P = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

// Крупная линейная иконка для пустых состояний (те же контуры, что в навигации,
// тоньше и цветом вторичного текста) — вместо разнобойных эмодзи.
export function EmptyStateIcon({ name, size = 56, color = 'var(--text-secondary)' }) {
  const icon = NAV_LINE_ICONS[name];
  if (!icon) return null;
  return React.cloneElement(icon, {
    width: size,
    height: size,
    strokeWidth: 1.5,
    style: { color },
  });
}

// Кольцевой лоадер в цветах бренда — вместо эмодзи-«песочных часов»
export function Spinner({ size = 40 }) {
  return (
    <div
      aria-label="Загрузка"
      style={{
        width: size, height: size, margin: '0 auto',
        border: '3px solid var(--border-color)',
        borderTopColor: 'var(--primary-color)',
        borderRadius: '50%',
        animation: 'spin 0.9s linear infinite',
      }}
    />
  );
}

// Мини-иконки статусов заказа (в цвет бейджа через currentColor) — вместо эмодзи
const STATUS_PATHS = {
  check: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>,
  x: <><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>,
  clock: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>,
  card: <><rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" /></>,
  truck: <><rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></>,
  box: <><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></>,
  refresh: <><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></>,
  pause: <><circle cx="12" cy="12" r="10" /><line x1="10" y1="15" x2="10" y2="9" /><line x1="14" y1="15" x2="14" y2="9" /></>,
  dot: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" /></>,
  star: <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />,
  calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
  pin: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></>,
  info: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>,
  chat: <><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /><circle cx="8" cy="11.5" r="1.2" fill="currentColor" stroke="none" /><circle cx="12" cy="11.5" r="1.2" fill="currentColor" stroke="none" /><circle cx="16" cy="11.5" r="1.2" fill="currentColor" stroke="none" /></>,
  edit: <path d="M17 3a2.83 2.83 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />,
  gift: <><polyline points="20 12 20 22 4 22 4 12" /><rect x="2" y="7" width="20" height="5" /><line x1="12" y1="22" x2="12" y2="7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" /></>,
};

// Крупные иконки статусных экранов и попапов (оплачен / не оплачен /
// уведомления) — та же линейная стилистика, что у нижней навигации и
// StatusIcon, вместо разнобойных эмодзи (✅ 😢 🦎 🔔). Формы согласованы
// 2026-08-17: НЕ галочка в круге (читалась как «Сбер-2000») и НЕ крестик
// («агрессивно») — пакет покупки с галочкой и часы «время оплаты вышло».
// Тон нейтральный, var(--text-secondary) бренда — как у EmptyStateIcon.
const HERO_PATHS = {
  paid: <><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><polyline points="8.5 13.5 11 16 15.5 11.5" /></>,
  unpaid: STATUS_PATHS.clock,
  bell: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></>,
  // Товара не хватило: корзина с минусом — тост говорит не про склад вообще,
  // а про корзину покупателя, из которой позицию убрали или урезали
  soldOut: <><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /><line x1="11" y1="9" x2="17" y2="9" /></>,
  lock: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
  link: <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>,
  alert: <><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>,
  clip: <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />,
  pin: STATUS_PATHS.pin,
};

// inline=true — иконка в строке текста (рядом с надписью), без центрирования
export function HeroIcon({ name, size = 56, color = 'var(--text-secondary)', strokeWidth = 1.5, inline = false }) {
  const paths = HERO_PATHS[name];
  if (!paths) return null;
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
      style={inline
        ? { color, display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }
        : { color, display: 'block', margin: '0 auto' }}
    >
      {paths}
    </svg>
  );
}

export function StatusIcon({ name, size = 13 }) {
  const paths = STATUS_PATHS[name];
  if (!paths) return null;
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
      style={{ verticalAlign: '-2px', marginRight: 4 }}
    >
      {paths}
    </svg>
  );
}

// Единый лоадер раздела (каталог/корзина/заказы/профиль): фирменный спиннер +
// одна нейтральная подпись — разделы грузятся одинаково.
export function SectionLoader({ label = 'Загружаем…' }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: 14, padding: '48px 20px', minHeight: '45vh',
    }}>
      <Spinner size={36} />
      <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{label}</div>
    </div>
  );
}

// Иконка поля формы (календарь/часы в пикерах даты и времени) — без внешних
// отступов, красится цветом текста через currentColor.
export function FieldIcon({ name, size = 18 }) {
  const paths = STATUS_PATHS[name];
  if (!paths) return null;
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
      style={{ display: 'block' }}
    >
      {paths}
    </svg>
  );
}

// Статус-бейдж: прямоугольная плашка с линейной иконкой. С color — мягкий
// цветной фон + цветной текст (хороший/плохой статус читается цветом),
// без color — нейтральная контурная.
export function StatusBadge({ icon, label, color }) {
  return (
    <span style={{
      fontSize: 12, fontWeight: 500,
      padding: '4px 8px',
      borderRadius: 6,
      ...(color
        ? { background: color + '20', color }
        : { border: '1px solid #D9D9D6', color: 'var(--text-color, #1A1A1A)' }),
      display: 'inline-flex', alignItems: 'center',
      whiteSpace: 'nowrap',
    }}>
      {icon && <StatusIcon name={icon} />}{label}
    </span>
  );
}

export const NAV_LINE_ICONS = {
  menu: (
    <svg {...P}>
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  ),
  cart: (
    <svg {...P}>
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
  ),
  orders: (
    <svg {...P}>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  profile: (
    <svg {...P}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  about: (
    <svg {...P}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  gift: (
    <svg {...P}>
      <polyline points="20 12 20 22 4 22 4 12" />
      <rect x="2" y="7" width="20" height="5" />
      <line x1="12" y1="22" x2="12" y2="7" />
      <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
      <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
    </svg>
  ),
};
